/**
 * Products Cache Service
 * Manages offline caching and real-time sync of products1 and products2 data
 */

import { subscribeToCollection } from '../db_calls';
import { getCollectionPath } from '../utils/dbPaths';
import { useProducts } from '../stores';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize products cache system
 * Loads products1 and products2 once on init (not real-time)
 * @returns {Object} Object with unsubscribe functions (empty for consistency)
 */
export const initializeProductsCache = () => {
  // REAL-TIME, not a one-shot read. Previously this loaded once at startup, so a price
  // or quantity edited in Settings did not appear anywhere until a full page refresh —
  // and the booking flows quoted from a stale catalog in the meantime.
  const unsubscribeProducts1 = subscribeToCollection(
    getCollectionPath('products1'),
    (products1, error) => {
      if (error) {
        console.error('❌ Error in products1 subscription:', error);
        return;
      }
      useProducts.getState().setProducts1(products1 || []);
    }
  );

  const unsubscribeProducts2 = subscribeToCollection(
    getCollectionPath('products2'),
    (products2, error) => {
      if (error) {
        console.error('❌ Error in products2 subscription:', error);
        return;
      }
      // products2 renders in a user-defined order; sort here so every consumer
      // gets the same ordering without repeating it.
      const sorted = [...(products2 || [])].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
        return 0;
      });
      useProducts.getState().setProducts2(sorted);
    }
  );

  return { unsubscribeProducts1, unsubscribeProducts2 };
};

// ==================== PRODUCTS1 CACHE FUNCTIONS ====================

/**
 * Get all products1 from cache (synchronous)
 * @returns {Array} Array of product objects
 */
export const getProducts1FromCache = () => {
  try {
    const products = useProducts.getState().products1;
    return products;
  } catch (error) {
    console.error('❌ Error reading products1 from cache:', error);
    return [];
  }
};

// localStorage caching functions removed - using Firestore persistent cache only

// ==================== PRODUCTS2 CACHE FUNCTIONS ====================

/**
 * Get all products2 from cache (synchronous)
 * @returns {Array} Array of product objects
 */
export const getProducts2FromCache = () => {
  try {
    const products = useProducts.getState().products2;
    return products;
  } catch (error) {
    console.error('❌ Error reading products2 from cache:', error);
    return [];
  }
};

// localStorage caching functions removed - using Firestore persistent cache only

// ==================== COMBINED CACHE UTILITIES ====================

/**
 * Clear all products cache
 */
export const clearProductsCache = () => {
  try {
    useProducts.getState().clearProducts();
  } catch (error) {
    console.error('❌ Error clearing products cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getProductsCacheStats = () => {
  const products1 = getProducts1FromCache();
  const products2 = getProducts2FromCache();
  
  return {
    products1Count: products1.length,
    products2Count: products2.length,
    products1LastUpdated: null, // No longer tracking timestamp - using Firestore cache
    products2LastUpdated: null, // No longer tracking timestamp - using Firestore cache
  };
};

/**
 * Update a single product in products1 cache
 * @param {string} productId - Product ID
 * @param {Object} updates - Fields to update
 */
export const updateProduct1InCache = (productId, updates) => {
  try {
    const products = getProducts1FromCache();
    const index = products.findIndex(p => p.id === productId);
    
    if (index !== -1) {
      const updatedProducts = [...products];
      updatedProducts[index] = { ...updatedProducts[index], ...updates };
      useProducts.getState().setProducts1(updatedProducts);
    }
  } catch (error) {
    console.error('❌ Error updating product1 in cache:', error);
  }
};

/**
 * Update a single product in products2 cache
 * @param {string} productId - Product ID
 * @param {Object} updates - Fields to update
 */
export const updateProduct2InCache = (productId, updates) => {
  try {
    const products = getProducts2FromCache();
    const index = products.findIndex(p => p.id === productId);
    
    if (index !== -1) {
      products[index] = { ...products[index], ...updates };
      
      // Re-sort by order field
      const sortedProducts = [...products].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return 0;
      });
      
      useProducts.getState().setProducts2(sortedProducts);
    }
  } catch (error) {
    console.error('❌ Error updating product2 in cache:', error);
  }
};

/**
 * Add a new product to products1 cache
 * @param {Object} product - Product object
 */
export const addProduct1ToCache = (product) => {
  try {
    const products = getProducts1FromCache();
    const updatedProducts = [...products, product];
    useProducts.getState().setProducts1(updatedProducts);
  } catch (error) {
    console.error('❌ Error adding product1 to cache:', error);
  }
};

/**
 * Add a new product to products2 cache
 * @param {Object} product - Product object
 */
export const addProduct2ToCache = (product) => {
  try {
    const products = getProducts2FromCache();
    products.push(product);
    
    // Re-sort by order field
    const sortedProducts = [...products].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return 0;
    });
    
    useProducts.getState().setProducts2(sortedProducts);
  } catch (error) {
    console.error('❌ Error adding product2 to cache:', error);
  }
};

/**
 * Remove a product from products1 cache
 * @param {string} productId - Product ID
 */
export const removeProduct1FromCache = (productId) => {
  try {
    const products = getProducts1FromCache();
    const filtered = products.filter(p => p.id !== productId);
    
    if (filtered.length < products.length) {
      useProducts.getState().setProducts1(filtered);
    }
  } catch (error) {
    console.error('❌ Error removing product1 from cache:', error);
  }
};

/**
 * Remove a product from products2 cache
 * @param {string} productId - Product ID
 */
export const removeProduct2FromCache = (productId) => {
  try {
    const products = getProducts2FromCache();
    const filtered = products.filter(p => p.id !== productId);
    useProducts.getState().setProducts2(filtered);
  } catch (error) {
    console.error('❌ Error removing product2 from cache:', error);
  }
};


